import React, { forwardRef, useCallback } from 'react';
import { useAddPaymentMethod } from '@features/subscriptions/hooks/useAddPaymentMethod';
import { useGetPaymentMethods } from '@features/subscriptions/hooks/useGetPaymentMethods';
import { useUpdateDefaultPaymentMethod } from '@features/subscriptions/hooks/useUpdateDefaultPaymentMethod';
import { DrawerForm } from '@goodfynd/forms';
import type { DrawerRefMethods } from '@goodfynd/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import { ChangePaymentMethodForm } from './ChangePaymentMethodForm';
import type {
  ChangePaymentMethodFormDrawerProps,
  PaymentMethodFormFields,
} from './types';
import { validationSchema } from './validation';

export const ChangePaymentMethodFormDrawer = forwardRef(
  function ChangePaymentMethodFormDrawer(
    {
      organizationId,
      defaultPaymentMethodId,
      onSaved,
      ...props
    }: ChangePaymentMethodFormDrawerProps,
    ref: React.ForwardedRef<DrawerRefMethods | null>
  ) {
    const [isOpen, setIsOpen] = React.useState(false);
    const { paymentMethods, isLoading } = useGetPaymentMethods(organizationId, {
      enabled: isOpen,
    });
    const { updateDefaultPaymentMethod } =
      useUpdateDefaultPaymentMethod(organizationId);
    const { addPaymentMethod } = useAddPaymentMethod(organizationId);

    const handleSuccess = useCallback(() => {
      onSaved?.();
    }, [onSaved]);

    return (
      <DrawerForm<PaymentMethodFormFields>
        {...props}
        busy={isLoading}
        hideTarget
        ref={ref}
        resolver={zodResolver(validationSchema)}
        targetContent="Update payment method"
        title="Change Payment Method"
        defaultValues={{
          paymentMethodId: defaultPaymentMethodId ?? '',
        }}
        onClose={() => {
          setIsOpen(false);
        }}
        onOpen={() => {
          setIsOpen(true);
        }}
        onSubmit={async (values) => {
          let paymentMethodId = values.paymentMethodId;

          if (
            paymentMethodId === 'add-new-card' &&
            values.stripePaymentMethodId
          ) {
            await addPaymentMethod(values.stripePaymentMethodId);
            paymentMethodId = values.stripePaymentMethodId;
          }

          await updateDefaultPaymentMethod({ paymentMethodId });
          handleSuccess();
        }}
        renderFields={(formProps) => (
          <ChangePaymentMethodForm
            {...formProps}
            paymentMethods={paymentMethods}
          />
        )}
      />
    );
  }
);
